import { isEmpty } from "@utils/var.check.ts";
import SecurityUtils from "@utils/security.utils.ts";
import StringUtils from "@utils/string.utils.ts";
import SearchResourceDetailContentSubpage from "@modules/search/components/SearchResourceDetailContentSubpage.vue";
import { MEDIA_TYPE } from "@modules/search/service/search.constant.ts";
import DateTimeFormatter from "@utils/datetime.formatter.ts";
import { useErrorHandler } from "@plugins/common/composables/error.ts";
import mediaObjectApi from "@apis/media.object.api.ts";
import { performFileDownload } from "@utils/download.utils.ts";
import { useContentSubpageInstance } from "@plugins/content-subpage/composables/content-subpage-instance.ts";
import ResourceSearchCardUtils from "@utils/resource.search.card.utils.ts";
import {
  getLocationLabel,
  RESOURCE_SEARCH_TYPE,
} from "@modules/resource-search/resource-search-helper.ts";
import {
  MediaResultItem,
  MediaResultItemHighlights,
  MediaSearchCardProps,
  TranscriptHighlight,
} from "@modules/resource-search/resource-search-types.ts";
import { useI18n } from "vue-i18n";

export const useMediaSearchCard = () => {
  const contentSubpageInstance = useContentSubpageInstance();
  const { passiveApiFailHandler } = useErrorHandler();
  const { t } = useI18n();

  const populateMediaSearchCardProps = (resourceData: MediaResultItem): MediaSearchCardProps => {
    // populate card props from response params
    return {
      uuid: resourceData?.uuid,
      labelConfig: {
        kind: "domain",
      },
      resourceDomain: resourceData?.resourceDomain,
      title: resourceData?.title,
      titleHighlighted: getTitle(resourceData?.title, resourceData?.highlights),
      type: RESOURCE_SEARCH_TYPE.MEDIA,
      resourceType: resourceData?.objectType,
      highlights: resourceData?.highlights,
      content: getInformationBody(
        resourceData?.description,
        resourceData?.highlights,
        resourceData?.objectType,
      ),
      downloadable: resourceData?.downloadable,
      src: ResourceSearchCardUtils.getSrc(
        resourceData?.objectType,
        resourceData?.fileUrl,
        resourceData?.thumbnailUrl,
      ),
      ariaLabelConfig: {
        cardComponent: t("search_view_details_aria_label", {
          title: SecurityUtils.stripHtml(resourceData?.title),
        }),
        download: t("search_result_module_card_download_aria_label"),
      },
    };
  };

  const populateMediaSearchCardListeners = (resourceData: MediaSearchCardProps) => {
    return {
      click: () => handleViewDetails(resourceData),
      download: () => handleDownload(resourceData?.uuid),
    };
  };

  const getTitle = (title: string, highlights: MediaResultItemHighlights) => {
    if (!isEmpty(highlights.title)) {
      return SecurityUtils.sanitizeHTMLForHighlight(highlights.title);
    }
    return SecurityUtils.sanitizeHTMLForHighlight(title ? title : "");
  };

  const getInformationBody = (
    description: string,
    highlights: MediaResultItemHighlights,
    mediaObjectType: string,
  ) => {
    if (!isEmpty(highlights.description)) {
      return (
        "[ <span>Description</span> ] " +
        SecurityUtils.sanitizeHTMLForHighlight(highlights.description)
      );
    }

    if (!isEmpty(highlights.keyword)) {
      return (
        "[ <span>Tags</span> ] " +
        SecurityUtils.sanitizeHTMLForHighlight(
          StringUtils.formatSpaceAfterComma(highlights.keyword),
        )
      );
    }

    if (
      (mediaObjectType === MEDIA_TYPE.VIDEO || mediaObjectType === MEDIA_TYPE.AUDIO) &&
      !isEmpty(highlights.transcript)
    ) {
      return (
        "[ <span>Transcript</span> ] " +
        SecurityUtils.sanitizeHTMLForHighlight(getTranscript(highlights.transcript))
      );
    }

    return SecurityUtils.sanitizeHTMLForHighlight(description ? description : "");
  };

  const getTranscript = (transcript: TranscriptHighlight) => {
    const startTimeInSecond = Math.floor(transcript.startTime / 1000);
    let startTimePart = DateTimeFormatter.formatSecondsToTime24hrSeconds(startTimeInSecond);

    if (startTimeInSecond < 3600) {
      startTimePart = startTimePart.substring(startTimePart.length - 5);
    }

    return `${startTimePart} - ${transcript.transcriptText}`;
  };

  const handleDownload = async (uuid: string) => {
    try {
      const response = await mediaObjectApi.downloadMediaObject({ uuid: uuid });
      if (response.code === "ok") {
        performFileDownload(response.data);
        return;
      }
      passiveApiFailHandler();
    } catch (e) {
      passiveApiFailHandler(e);
    }
  };

  const handleViewDetails = (resourceData: MediaSearchCardProps) => {
    contentSubpageInstance.show(SearchResourceDetailContentSubpage, {
      highlights: resourceData.highlights,
      baseTitle: [],
      uuid: resourceData.uuid,
      title: resourceData.title,
      location: getLocationLabel(resourceData.resourceDomain),
      type: RESOURCE_SEARCH_TYPE.MEDIA,
      hasSelectAction: false, // Show Choose button
      addFromLocation: null,
    });
  };

  return {
    populateMediaSearchCardProps,
    populateMediaSearchCardListeners,
    handleDownload,
  };
};
